import {
  AlignCenterOutlined,
  AlignLeftOutlined,
  AlignRightOutlined,
  BoldOutlined,
  CodeOutlined,
  FontSizeOutlined,
  ItalicOutlined,
  LineHeightOutlined,
  MenuOutlined,
  OrderedListOutlined,
  UnderlineOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { Card, Select, Space } from "antd";
import isHotkey from "is-hotkey";
import { useCallback, useMemo } from "react";
import { Descendant, Editor, Element as SlateElement, Transforms, createEditor } from "slate";
import { withHistory } from "slate-history";
import { Editable, Slate, useSlate, withReact } from "slate-react";
import { dictionary, formatField, i18n } from "src/utils";
import { Button, Icon, Toolbar } from "../RichTextEditor/Components";

const HOTKEYS = {
  "mod+b": "bold",
  "mod+i": "italic",
  "mod+u": "underline",
  "mod+`": "code",
};

const LIST_TYPES = ["numbered-list", "bulleted-list"];
const TEXT_ALIGN_TYPES = ["left", "center", "right", "justify"];

interface RichTextEditorSlateProps {
  content: Descendant[];
  fieldList?: any[];
  placeHolder: string;
  style?: any;
  onChange?: ((value: Descendant[]) => void) | undefined;
  selectWithMarginTop?: boolean;
}

const RichTextEditorSlate = (props: RichTextEditorSlateProps) => {
  const renderElement = useCallback((props: any) => <Element {...props} />, []);
  const renderLeaf = useCallback((props: any) => <Leaf {...props} />, []);
  const editor = useMemo(() => withHistory(withReact(createEditor())), []);

  return (
    <Card className="card-with-custom-padding">
      <Slate
        editor={editor}
        initialValue={props.content}
        onChange={props.onChange}
      >
        <Toolbar>
          <MarkButton
            format="bold"
            icon={<BoldOutlined />}
          />
          <MarkButton
            format="italic"
            icon={<ItalicOutlined />}
          />
          <MarkButton
            format="underline"
            icon={<UnderlineOutlined />}
          />
          <MarkButton
            format="code"
            icon={<CodeOutlined />}
          />
          <BlockButton
            format="heading-one"
            icon={<LineHeightOutlined />}
          />
          <BlockButton
            format="heading-two"
            icon={<FontSizeOutlined />}
          />
          <BlockButton
            format="numbered-list"
            icon={<OrderedListOutlined />}
          />
          <BlockButton
            format="bulleted-list"
            icon={<UnorderedListOutlined />}
          />
          <BlockButton
            format="left"
            icon={<AlignLeftOutlined />}
          />
          <BlockButton
            format="center"
            icon={<AlignCenterOutlined />}
          />
          <BlockButton
            format="right"
            icon={<AlignRightOutlined />}
          />
          <BlockButton
            format="justify"
            icon={<MenuOutlined />}
            withMarginRight
          />
          {props.fieldList && (
            <Space
              wrap
              style={{ marginLeft: 0, marginTop: props.selectWithMarginTop ? 5 : 0 }}
            >
              <FieldSelect
                {...props}
                format="field"
              />
            </Space>
          )}
        </Toolbar>

        <Editable
          renderElement={renderElement}
          renderLeaf={renderLeaf}
          placeholder={props.placeHolder}
          spellCheck
          autoFocus
          style={{
            ...props.style,
            overflow: "auto",
            padding: "8px 8px",
            minHeight: "200px",
            // outline: "solid 2px",
          }}
          onKeyDown={(event) => {
            for (const hotkey in HOTKEYS) {
              if (isHotkey(hotkey, event as any)) {
                event.preventDefault();
                const mark = (HOTKEYS as any)[hotkey];
                toggleMark(editor, mark);
              }
            }
          }}
        />
      </Slate>
    </Card>
  );
};

const toggleBlock = (editor: any, format: any) => {
  const isActive = isBlockActive(editor, format, TEXT_ALIGN_TYPES.includes(format) ? "align" : "type");
  const isList = LIST_TYPES.includes(format);

  Transforms.unwrapNodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      LIST_TYPES.includes(n.type) &&
      !TEXT_ALIGN_TYPES.includes(format),
    split: true,
  });
  let newProperties: Partial<SlateElement>;
  if (TEXT_ALIGN_TYPES.includes(format)) {
    newProperties = {
      align: isActive ? undefined : format,
    };
  } else {
    newProperties = {
      type: isActive ? "paragraph" : isList ? "list-item" : format,
    };
  }
  Transforms.setNodes<SlateElement>(editor, newProperties);

  if (!isActive && isList) {
    const block = { type: format, children: [] };
    Transforms.wrapNodes(editor, block);
  }
};

const toggleMark = (editor: any, format: any) => {
  const isActive = isMarkActive(editor, format);

  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

const isBlockActive = (editor: any, format: any, blockType = "type") => {
  const { selection } = editor;
  if (!selection) return false;

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && (n as any)[blockType] === format,
    }),
  );

  return !!match;
};

const isMarkActive = (editor: any, format: any) => {
  const marks = Editor.marks(editor);
  return marks ? (marks as any)[format] === true : false;
};

const Element = ({ attributes, children, element }: { attributes: any; children: any; element: any }) => {
  const style = { textAlign: element.align };
  switch (element.type) {
    case "block-quote":
      return (
        <blockquote
          style={style}
          {...attributes}
        >
          {children}
        </blockquote>
      );
    case "bulleted-list":
      return (
        <ul
          style={style}
          {...attributes}
        >
          {children}
        </ul>
      );
    case "heading-one":
      return (
        <h1
          style={style}
          {...attributes}
        >
          {children}
        </h1>
      );
    case "heading-two":
      return (
        <h2
          style={style}
          {...attributes}
        >
          {children}
        </h2>
      );
    case "list-item":
      return (
        <li
          style={style}
          {...attributes}
        >
          {children}
        </li>
      );
    case "numbered-list":
      return (
        <ol
          style={style}
          {...attributes}
        >
          {children}
        </ol>
      );
    default:
      return (
        <p
          style={style}
          {...attributes}
        >
          {children}
        </p>
      );
  }
};

const Leaf = ({ attributes, children, leaf }: { attributes: any; children: any; leaf: any }) => {
  if (leaf.bold) {
    children = <strong>{children}</strong>;
  }

  if (leaf.code) {
    children = <code>{children}</code>;
  }

  if (leaf.italic) {
    children = <em>{children}</em>;
  }

  if (leaf.underline) {
    children = <u>{children}</u>;
  }

  return <span {...attributes}>{children}</span>;
};

const BlockButton = ({ format, icon, withMarginRight }: { format: any; icon: any; withMarginRight?: boolean }) => {
  const editor = useSlate();
  return (
    <Button
      style={{ marginRight: withMarginRight ? 16 : 0 }}
      active={isBlockActive(editor, format, TEXT_ALIGN_TYPES.includes(format) ? "align" : "type")}
      onMouseDown={(event: any) => {
        event.preventDefault();
        toggleBlock(editor, format);
      }}
    >
      <Icon>{icon}</Icon>
    </Button>
  );
};

const MarkButton = ({ format, icon }: { format: any; icon: any }) => {
  const editor = useSlate();
  return (
    <Button
      active={isMarkActive(editor, format)}
      onMouseDown={(event: any) => {
        event.preventDefault();
        toggleMark(editor, format);
      }}
    >
      <Icon>{icon}</Icon>
    </Button>
  );
};

const FieldSelect = (props: any) => {
  const editor = useSlate();

  const handleChange = (value: string) => {
    const field = formatField(value);
    editor.insertText(field);

    if (editor.selection) {
      editor.setSelection({
        anchor: editor.selection.anchor,
        focus: { path: editor.selection.anchor.path, offset: -field.length },
      });
      editor.addMark(props.format, true);

      editor.setSelection({
        anchor: editor.selection.anchor,
        focus: { path: editor.selection.anchor.path, offset: 0 },
      });
      editor.addMark(props.format, false);
    }
  };

  return (
    <Select
      onChange={handleChange}
      options={props.fieldList.map((field: any) => {
        return {
          label: i18n(dictionary.emailBodyTextField.label.concat(field)),
          value: i18n(dictionary.emailBodyTextField.value.concat(field)),
        };
      })}
      value={i18n(dictionary.insert, { item: i18n(dictionary.field) })}
      onMouseDown={(event: any) => {
        event.preventDefault();
      }}
      disabled={isMarkActive(editor, props.format)}
      showSearch
    />
  );
};

export default RichTextEditorSlate;
